import {
  AUTOMATION_TRIGGER_TYPES,
  type AutomationTrigger,
  CUSTOM_EVENT_TYPE_PATTERN,
  DATE_FIELD_OFFSET_MAX_DAYS,
  DATE_SYSTEM_FIELDS,
  EVENT_TYPES,
  isValidCron,
  type SlaPolicy,
  TRIGGER_EXCLUDED_EVENT_TYPES,
} from '@servicetim/shared';
import { Input } from '@servicetim/ui/components';
import { useState } from 'react';
import type { FieldCatalog } from '../projects/catalog/field-catalog';
import { FormRow } from '../projects/ui/form-row';
import { SimpleSelect } from '../projects/ui/simple-select';
import { TimeZoneInput } from '../tenant/common/time-zone-input';
import { FilterBuilder } from '../views/query-builder/filter-builder';

const TRIGGER_TYPE_LABELS: Record<(typeof AUTOMATION_TRIGGER_TYPES)[number], string> = {
  event: 'A domain event happens',
  schedule: 'On a schedule',
  'sla.breached': 'An SLA breaches',
  date_field: 'A date field is reached',
};

const CUSTOM_EVENT_OPTION = '__custom__';

const EVENT_TYPE_OPTIONS = EVENT_TYPES.filter(
  (t) => !(TRIGGER_EXCLUDED_EVENT_TYPES as readonly string[]).includes(t),
).map((t) => ({ value: t, label: t, group: t.split('.')[0] }));

const isCustomEventType = (eventType: string): boolean => eventType.startsWith('custom.');

export function newTrigger(type: (typeof AUTOMATION_TRIGGER_TYPES)[number]): AutomationTrigger {
  switch (type) {
    case 'event':
      return { type, eventType: 'ticket.created' };
    case 'schedule':
      return { type, cron: '0 9 * * MON-FRI', timeZone: 'UTC' };
    case 'sla.breached':
      return { type };
    case 'date_field':
      return { type, field: 'due_at', offsetDays: 0 };
  }
}

export interface TriggerEditorProps {
  value: AutomationTrigger;
  onChange: (next: AutomationTrigger) => void;
  catalog: FieldCatalog;
  slaPolicies: readonly SlaPolicy[];
  idPrefix: string;
}

/** The `trigger` half of a rule: type plus its type-specific fields (S-07-05 … S-07-08). */
export function TriggerEditor({
  value,
  onChange,
  catalog,
  slaPolicies,
  idPrefix,
}: TriggerEditorProps) {
  const dateFieldOptions = [
    ...DATE_SYSTEM_FIELDS.map((f) => ({ value: f, label: f })),
    ...catalog.custom
      .filter((f) => f.type === 'date' || f.type === 'datetime')
      .map((f) => ({ value: f.key, label: f.label })),
  ];

  return (
    <div className="space-y-4">
      <FormRow label="When" htmlFor={`${idPrefix}-type`}>
        <SimpleSelect
          id={`${idPrefix}-type`}
          value={value.type}
          options={AUTOMATION_TRIGGER_TYPES.map((t) => ({
            value: t,
            label: TRIGGER_TYPE_LABELS[t],
          }))}
          onChange={(next) => next && onChange(newTrigger(next as typeof value.type))}
        />
      </FormRow>

      {value.type === 'event' ? (
        <>
          <EventTypePicker
            idPrefix={idPrefix}
            eventType={value.eventType}
            onChange={(eventType) => onChange({ type: 'event', eventType, filter: value.filter })}
          />
          <FormRow
            label="Only when (optional)"
            hint="Narrows the trigger to tickets matching this filter; empty means every occurrence fires the rule."
          >
            <FilterBuilder
              idPrefix={`${idPrefix}-filter`}
              value={value.filter}
              onChange={(filter) => onChange({ ...value, filter })}
              catalog={catalog}
            />
          </FormRow>
        </>
      ) : null}

      {value.type === 'schedule' ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <FormRow
            label="Cron expression"
            htmlFor={`${idPrefix}-cron`}
            hint="Five fields: minute hour day-of-month month day-of-week (UTC-less; interpreted in the time zone below)."
            error={
              value.cron !== '' && !isValidCron(value.cron)
                ? 'Not a valid cron expression'
                : undefined
            }
          >
            <Input
              id={`${idPrefix}-cron`}
              value={value.cron}
              maxLength={100}
              className="font-mono"
              onChange={(e) => onChange({ ...value, cron: e.target.value })}
            />
          </FormRow>
          <FormRow label="Time zone" htmlFor={`${idPrefix}-tz`}>
            <TimeZoneInput
              id={`${idPrefix}-tz`}
              value={value.timeZone}
              onChange={(e) => onChange({ ...value, timeZone: e.target.value })}
            />
          </FormRow>
        </div>
      ) : null}

      {value.type === 'sla.breached' ? (
        <FormRow
          label="Policy (optional)"
          htmlFor={`${idPrefix}-policy`}
          hint="Leave empty to fire for any SLA policy of this project."
        >
          <SimpleSelect
            id={`${idPrefix}-policy`}
            value={value.policyId}
            emptyLabel="Any policy"
            options={slaPolicies.map((p) => ({ value: p.id, label: p.name }))}
            onChange={(policyId) => onChange({ type: 'sla.breached', policyId })}
          />
        </FormRow>
      ) : null}

      {value.type === 'date_field' ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <FormRow label="Date field" htmlFor={`${idPrefix}-field`}>
            <SimpleSelect
              id={`${idPrefix}-field`}
              value={value.field}
              options={dateFieldOptions}
              onChange={(field) => field && onChange({ ...value, field })}
            />
          </FormRow>
          <FormRow
            label="Offset (days)"
            htmlFor={`${idPrefix}-offset`}
            hint="Negative fires before the date, positive after; 0 fires on the day."
          >
            <Input
              id={`${idPrefix}-offset`}
              type="number"
              min={-DATE_FIELD_OFFSET_MAX_DAYS}
              max={DATE_FIELD_OFFSET_MAX_DAYS}
              value={value.offsetDays}
              onChange={(e) => onChange({ ...value, offsetDays: Number(e.target.value) || 0 })}
            />
          </FormRow>
        </div>
      ) : null}
    </div>
  );
}

function EventTypePicker({
  idPrefix,
  eventType,
  onChange,
}: {
  idPrefix: string;
  eventType: string;
  onChange: (eventType: string) => void;
}) {
  const [customMode, setCustomMode] = useState(() => isCustomEventType(eventType));
  const selectValue = customMode ? CUSTOM_EVENT_OPTION : eventType;
  return (
    <FormRow label="Event type" htmlFor={`${idPrefix}-event-type`}>
      <div className="flex flex-wrap items-center gap-2">
        <SimpleSelect
          id={`${idPrefix}-event-type`}
          value={selectValue}
          options={[
            ...EVENT_TYPE_OPTIONS,
            { value: CUSTOM_EVENT_OPTION, label: 'Custom event (custom.*)', group: 'custom' },
          ]}
          onChange={(next) => {
            if (!next) return;
            if (next === CUSTOM_EVENT_OPTION) {
              setCustomMode(true);
              onChange('custom.');
            } else {
              setCustomMode(false);
              onChange(next);
            }
          }}
        />
        {customMode ? (
          <Input
            aria-label="Custom event type"
            value={eventType}
            maxLength={80}
            placeholder="custom.something_happened"
            className="w-64 font-mono text-xs"
            onChange={(e) => onChange(e.target.value)}
            aria-invalid={!CUSTOM_EVENT_TYPE_PATTERN.test(eventType) || undefined}
          />
        ) : null}
      </div>
    </FormRow>
  );
}
